/** Wall-local architectural construction. Apertures drive both wall solids and
 * their attached surrounds; frames retain their thickness as bays resize. */
import type {AssetVec3} from '../proceduralAssets.js';
import type {FrontierBuilder} from './frontierExtras.js';
export type OpeningFamily = 'casement' | 'shuttered' | 'transom' | 'shop' | 'door';
export interface FacadeOpening { id:string; u:number; bottom:number; width:number; height:number; family:OpeningFamily }
export interface FacadePlan {
  id:string; center:AssetVec3; axis:'x'|'z'; outward:1|-1;
  width:number; height:number; thickness:number; openings:FacadeOpening[];
}
export function facadePoint(f:FacadePlan,u:number,y:number,depth=0):AssetVec3 {
  return f.axis==='x'?[f.center[0]+u,y,f.center[2]+depth*f.outward]:[f.center[0]+depth*f.outward,y,f.center[2]+u];
}
export function buildFacade(b:Pick<FrontierBuilder,'part'|'box'>,f:FacadePlan):void {
  const holes=[...f.openings].sort((a,c)=>a.u-c.u),lo=f.center[1],top=lo+f.height;
  if(![...f.center,f.width,f.height,f.thickness].every(Number.isFinite)||f.width<=0||f.height<=0||f.thickness<=0||new Set(holes.map(o=>o.id)).size!==holes.length)throw Error(`Invalid facade ${f.id}`);
  let edge=-f.width/2;
  for(const o of holes){
    if(![o.u,o.bottom,o.width,o.height].every(Number.isFinite)||o.width<=0||o.height<=0||o.bottom<lo||o.bottom+o.height>top||o.u-o.width/2<edge-.00001||o.u+o.width/2>f.width/2)throw Error(`Invalid aperture ${f.id}/${o.id}`);
    edge=o.u+o.width/2;
  }
  const box=(id:string,u:number,y:number,w:number,h:number,depth:number,offset:number,mat:number,solid=false)=>{
    if(w<.00001||h<.00001)return;
    b.part(`${f.id}:${id}`,()=>b.box(f.axis==='x'?[w,h,depth]:[depth,h,w],facadePoint(f,u,y,offset),mat,solid));
  };
  const pier=(id:string,a:number,c:number)=>{
    box(id,(a+c)/2,(lo+top)/2,c-a,f.height,f.thickness,0,0,true);
    box(`${id}:plinth`,(a+c)/2,lo+.18,c-a,.36,f.thickness+.1,0,4);
  };
  edge=-f.width/2;
  for(const o of holes){
    const a=o.u-o.width/2,c=o.u+o.width/2,end=o.bottom+o.height;
    pier(`pier:${o.id}`,edge,a);
    box(`${o.id}:below`,o.u,(lo+o.bottom)/2,o.width,o.bottom-lo,f.thickness,0,0,true);
    box(`${o.id}:above`,o.u,(end+top)/2,o.width,top-end,f.thickness,0,0,true);
    if(o.bottom>lo+.36)box(`${o.id}:plinth`,o.u,lo+.18,o.width,.36,f.thickness+.1,0,4);
    // Deep jambs, lintel and sill surround a genuinely empty aperture.
    for(const [name,u] of [['left',a],['right',c]] as const)box(`${o.id}:${name}`,u,o.bottom+o.height/2,.12,o.height+.16,f.thickness+.13,.025,1);
    box(`${o.id}:lintel`,o.u,end+.045,o.width+.3,.18,f.thickness+.22,.04,1);
    box(`${o.id}:sill`,o.u,o.bottom-.045,o.width+.27,.14,f.thickness+.3,.05,4);
    if(o.family!=='door'&&o.family!=='shop'){
      if(o.family==='casement')box(`${o.id}:mullion`,o.u,o.bottom+o.height/2,.055,o.height,.07,0,1);
      if(o.family==='transom')box(`${o.id}:transom`,o.u,end-.32,o.width,.055,.07,0,1);
      // Open shutters have independently constructed rails and planks. They
      // fold alongside the wall, leaving the aperture and reveal unobstructed.
      const shutterW=Math.min(.36,o.width*.27);
      for(const [name,u] of [['left',a-.1-shutterW/2],['right',c+.1+shutterW/2]] as const){
        box(`${o.id}:shutter:${name}`,u,o.bottom+o.height/2,shutterW,o.height,.08,f.thickness/2+.08,5);
        for(const [rail,y] of [['bottom',o.bottom+.15],['top',end-.15]] as const)box(`${o.id}:rail:${name}:${rail}`,u,y,shutterW+.025,.065,.05,f.thickness/2+.14,1);
      }
    }
    edge=c;
  }
  pier('end',edge,f.width/2);
  box('wall-plate',0,top-.06,f.width+.12,.14,f.thickness+.12,0,1);
}
